import { Box, FormControlLabel, Paper, Switch, Typography } from '@mui/material';
import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { DataTableColumn, IStandardListViewExtraItems, StandardListViewV2 } from '../../../../../../Components/CoreLib/library';
import { CSVExportModal } from '../../../../../../Components/ExportCSV/CSVExportModal';
import { ImportWorkItemsModal } from '../../../../../../Components/Forms/ImportWorkItems';
import { DataFilterContext } from '../../../../../../Contexts';
import { WorkItemDto, WorkItemType } from '../../../../../../dtos';
import { useCSVExport } from '../../../../../../Hooks';
import { useGetWorkItemQuery, useWorkItemBulkArchiveMutation, useWorkItemBulkUnarchiveMutation } from '../../../../../../store';
import { formateDateStandard, toWords } from '../../../../../../util';
import { AddEditWorkItemModal } from '../../../../../Studies/Components/AddEditWorkItemModal';
import { BUSINESS_COMPONENT_SOFTWARE_TYPE_DISPLAY_VALUES, BUSINESS_COMPONENT_TYPE_DISPLAY_VALUES, DEFAULT_WORK_ITEM } from '../../../../../Studies/Hooks';
import { CompanyDashboardContext } from '../../../../Utils';
import { CSVColumnDefinition } from '../../../../../../models';

const DEFAULT_PAGINATION = { searchText: '', sortKey: 'NAME', sortAsc: true, page: 0, pageSize: 25, includeInactive: false };
const TABLE_COLUMNS: DataTableColumn<WorkItemDto>[] = [
    { key: 'name', label: 'Name', sortKey: 'NAME' },
    { key: 'type', label: 'Type', sortKey: 'TYPE', fieldMapper: (row) => toWords(WorkItemType[row.type]?.toString()) },
    { key: 'employeeCount', label: 'Employees', sortKey: '', unsorted: true },
    { key: 'startDate', label: 'Start Date', sortKey: 'START_DATE', fieldMapper: (row) => formateDateStandard(row.startDate) },
    { key: 'endDate', label: 'End Date', sortKey: 'END_DATE', fieldMapper: (row) => formateDateStandard(row.endDate, 'Ongoing') }
];

export const WORK_ITEM_CSV_COLUMN_DEFINITIONS: CSVColumnDefinition<WorkItemDto>[] = [
    { key: 'name', label: 'Business Component Name' },
    { key: 'startDate', label: 'Start Date', csvFormatter: (row: WorkItemDto) => formateDateStandard(row.startDate) },
    { key: 'endDate', label: 'End Date', csvFormatter: (row: WorkItemDto) => formateDateStandard(row.endDate, 'Ongoing') },
    { key: 'bcType', label: 'BC Type', csvFormatter: (row: WorkItemDto) => row.bcType !== undefined ? BUSINESS_COMPONENT_TYPE_DISPLAY_VALUES.get(row.bcType) : '' },
    { key: 'description', label: 'Project Description' },
    { key: 'bcSoftwareType', label: 'Software Type', csvFormatter: (row: WorkItemDto) => row.bcSoftwareType !== undefined ? BUSINESS_COMPONENT_SOFTWARE_TYPE_DISPLAY_VALUES.get(row.bcSoftwareType) : '' },
    { key: 'bcInformationSought', label: 'Information Sought to be Discovered' },
    { key: 'bcProcessOfExperimentation', label: 'Process of Experimentation' }
];

type StandardListViewV2EditHandler<T> = (id: string, record: T, event?: any) => void;

export const CompanyDashboardComponentsTab: FC = () => {
    const {company, setSelectedComponentId} = useContext(CompanyDashboardContext);
    const [isCompletedVisible, setIsCompletedVisible] = useState(true);
    const [isImportWorkItemModalVisible, setIsImportWorkItemModalVisible] = useState(false);
    const [selectedWorkItem, setSelectedWorkItem] = useState<WorkItemDto>();
    const {
        activeStatusFilter,
        itemTypeFilter,
        periodStartDateFilter,
        periodEndDateFilter
    } = useContext(DataFilterContext);

    const { openExportModal, isModalOpen, exportData, defaultFilename, columnDefinitions, closeModal } = useCSVExport(WORK_ITEM_CSV_COLUMN_DEFINITIONS);

    const customHeaderContent = useMemo(() => {
        return (
            <FormControlLabel
                style={{ marginRight: 1 }}
                control={<Switch size='small' checked={isCompletedVisible} onChange={(_, val) => setIsCompletedVisible(val)} />}
                label='Show Completed'
                labelPlacement='start'
            />
        );
    }, [isCompletedVisible]);

    const handleAdd = () => {
        setSelectedWorkItem(DEFAULT_WORK_ITEM);
    };

    const handleEdit: StandardListViewV2EditHandler<WorkItemDto> = (_, workItem) => {
        setSelectedWorkItem(workItem);
    };

    const handleRowClick: StandardListViewV2EditHandler<WorkItemDto> = useCallback((_, workItem) => {
        if(workItem && setSelectedComponentId) {
            setSelectedComponentId(workItem.id);
        }
    }, [setSelectedComponentId]);

    const handleExportToCSVClicked = useCallback((selectedItems: WorkItemDto[]) => {
        openExportModal(selectedItems, 'Components');
    }, [openExportModal]);

    const customMenuActions: IStandardListViewExtraItems[] = useMemo(() => [
        {
            enabledCheck: (selectedItems) => selectedItems.length >= 1,
            onClick: (selectedItems) => handleExportToCSVClicked(selectedItems),
            text: 'Export To CSV',
            clearSelectedItems: true,
        },
    ], [handleExportToCSVClicked]);

    return (
        <>
            <Paper elevation={0}>
                <Box height={16}></Box>
                <StandardListViewV2
                    headerIcon={<></>}
                    headerTitle=''
                    titleFontSize='32px'
                    defaultPaginationProps={DEFAULT_PAGINATION}
                    getDataQuery={useGetWorkItemQuery}
                    isShowActiveToggleVisible
                    activateMutation={useWorkItemBulkUnarchiveMutation}
                    inactivateMutation={useWorkItemBulkArchiveMutation}
                    inactivateConfirmationDialog={{
                        title: 'Delete Component Warning',
                        content: (
                            <>
                                <Typography>Deleting this component cannot be undone or recovered.</Typography>
                                <br />
                                <Typography>Are you sure you want to DELETE this component?</Typography>
                            </>
                        ),
                    }}
                    additionalMenuItems={customMenuActions}
                    tableColumns={TABLE_COLUMNS}
                    entityNameSingular='Component'
                    permissionName='workItems'
                    spacing={3}
                    additionalQueryParameters={{
                        companyId: company.id,
                        showCompleted: isCompletedVisible,
                        activeStatus: activeStatusFilter,
                        itemType: itemTypeFilter,
                        periodStartDate: periodStartDateFilter?.toISOString(),
                        periodEndDate: periodEndDateFilter?.toISOString(),
                    }}
                    preActionItemSlot={customHeaderContent}
                    handleAdd={handleAdd}
                    handleEdit={handleEdit}
                    handleRowClick={handleRowClick}
                    handleImportClicked={() => setIsImportWorkItemModalVisible(true)}
                    hideMenuDivider
                    hideSearch
                    hideFilterButton
                    disableGutters
                    hideBorder
                />
                <AddEditWorkItemModal
                    isOpen={!!selectedWorkItem}
                    workItemToUpdate={selectedWorkItem}
                    close={() => setSelectedWorkItem(undefined)}
                    companyId={company.id}
                />
                <ImportWorkItemsModal
                    companyId={company.id}
                    companyName={company.name}
                    onClose={() => setIsImportWorkItemModalVisible(false)}
                    isImportVisible={isImportWorkItemModalVisible}
                />
            </Paper>
            <CSVExportModal
                isOpen={isModalOpen}
                onClose={closeModal}
                data={exportData}
                columnDefinitions={columnDefinitions}
                defaultFilename={defaultFilename}
            />
        </>
    );
}